import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { fmtDate, fmtMoney, RECEIPT_STATUS } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

function NewReceiptDialog({ open, onClose, onDone }) {
  const [form, setForm] = useState({ po_id: "", received_pct: 100, note: "" });
  const [busy, setBusy] = useState(false);
  const { data: pos } = useQuery({
    queryKey: ["fin-pos-open"],
    queryFn: async () => {
      const all = (await api.get("/finanzas/purchase-orders")).data;
      return all.filter((p) => ["emitida", "recibida_proveedor", "confirmada", "parcialmente_ejecutada"].includes(p.status));
    },
  });
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/finanzas/receipts", { ...form, received_pct: Number(form.received_pct) });
      toast.success("Recepción registrada — pendiente de conformidad humana");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="new-receipt-dialog">
        <DialogHeader><DialogTitle>Registrar recepción</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Orden de compra *</Label>
            <Select value={form.po_id} onValueChange={(v) => setForm({ ...form, po_id: v })}>
              <SelectTrigger data-testid="receipt-po"><SelectValue placeholder="Selecciona orden abierta…" /></SelectTrigger>
              <SelectContent>{(pos || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.po_number} — {p.supplier_name} ({fmtMoney(p.total)})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>% recibido</Label><Input type="number" min="1" max="100" value={form.received_pct} onChange={(e) => setForm({ ...form, received_pct: e.target.value })} data-testid="receipt-pct" /></div>
          <div><Label>Nota</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="receipt-note" /></div>
          <Button className="w-full" onClick={submit} disabled={busy || !form.po_id} data-testid="receipt-submit">{busy ? "Registrando…" : "Registrar recepción"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConformityDialog({ receipt, mode, open, onClose, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [retained, setRetained] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/finanzas/receipts/${receipt.id}/action`, {
        action: mode, motivo: motivo || undefined, retained_amount: retained ? Number(retained) : undefined,
      });
      toast.success(mode === "aceptar" ? "Conformidad aceptada" : mode === "con_reservas" ? "Aceptada con reservas — excepción creada" : "Recepción rechazada — excepción creada");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="conformity-dialog">
        <DialogHeader><DialogTitle>{mode === "aceptar" ? "Aceptar conformidad" : mode === "con_reservas" ? "Aceptar con reservas" : "Rechazar recepción"} — {receipt?.receipt_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {mode !== "aceptar" && (
            <div><Label>Motivo *</Label><Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="conformity-motivo" /></div>
          )}
          {mode === "con_reservas" && (
            <div><Label>Importe retenido (€) *</Label><Input type="number" value={retained} onChange={(e) => setRetained(e.target.value)} data-testid="conformity-retained" /></div>
          )}
          <p className="text-xs text-slate-500">La conformidad es una decisión humana y alimenta el three-way match de la factura.</p>
          <Button className="w-full" variant={mode === "rechazar" ? "destructive" : "default"} onClick={submit}
            disabled={busy || (mode !== "aceptar" && !motivo.trim()) || (mode === "con_reservas" && !Number(retained))}
            data-testid="conformity-confirm">{busy ? "Registrando…" : "Confirmar"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Recepciones() {
  const { map } = useUsers();
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // {receipt, mode}
  const { data: items, refetch } = useQuery({
    queryKey: ["fin-receipts", status],
    queryFn: async () => (await api.get("/finanzas/receipts", { params: { status: status || undefined } })).data,
  });
  const actionable = (r) => ["pendiente", "pendiente_revision", "parcial", "recibida"].includes(r.status);

  return (
    <div data-testid="recepciones-page">
      <PageHeader
        title="Recepciones y conformidades"
        subtitle="Three-way match: sin conformidad aceptada, la factura no avanza a aprobación."
        actions={<Button onClick={() => setOpen(true)} data-testid="new-receipt-button"><Plus className="mr-1 h-4 w-4" /> Registrar recepción</Button>}
      />
      <div className="mb-4">
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="receipt-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(RECEIPT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {items && items.length === 0 && <EmptyState title="Sin recepciones" description="Registra la primera recepción sobre una orden emitida." />}
      {items?.length > 0 && (
        <div className="ts-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Recepción</TableHead><TableHead>Orden</TableHead><TableHead>Proveedor</TableHead><TableHead>%</TableHead><TableHead>Estado</TableHead><TableHead>Conformado por</TableHead><TableHead className="text-right">Retenido</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.id} data-testid={`receipt-row-${r.id}`}>
                  <TableCell className="font-mono text-xs">{r.receipt_number}</TableCell>
                  <TableCell className="font-mono text-xs">{r.po_number}</TableCell>
                  <TableCell className="text-sm">{r.supplier_name}</TableCell>
                  <TableCell className="text-xs">{r.received_pct}%</TableCell>
                  <TableCell><Chip map={RECEIPT_STATUS} value={r.status} testid={`receipt-status-${r.id}`} /></TableCell>
                  <TableCell className="text-xs">{r.accepted_by ? userName(map, r.accepted_by) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{r.retained_amount ? fmtMoney(r.retained_amount) : "—"}</TableCell>
                  <TableCell className="text-right">
                    {actionable(r) && (
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => setDialog({ receipt: r, mode: "aceptar" })} data-testid={`receipt-accept-${r.id}`}>Aceptar</Button>
                        <Button size="sm" variant="outline" onClick={() => setDialog({ receipt: r, mode: "con_reservas" })} data-testid={`receipt-reserve-${r.id}`}>Con reservas</Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setDialog({ receipt: r, mode: "rechazar" })} data-testid={`receipt-reject-${r.id}`}>Rechazar</Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {items?.some((r) => r.note) && (
        <p className="mt-3 text-xs text-slate-400">Las notas de recepción documentan incidencias que condicionan la conformidad.</p>
      )}
      <NewReceiptDialog open={open} onClose={() => setOpen(false)} onDone={() => { setOpen(false); refetch(); }} />
      {dialog && <ConformityDialog receipt={dialog.receipt} mode={dialog.mode} open onClose={() => setDialog(null)} onDone={() => { setDialog(null); refetch(); }} />}
    </div>
  );
}
